import { BaseElectionPost } from "./base/BaseElectionPost";
import {
  formatWithCommas, 
  roundToTwoDecimals, 
  convertirFecha 
} from "../utils/dataFormatter";

export class PresidentePost extends BaseElectionPost {
  constructor() {
    super({
      electionType: 'presidente',
      thresholds: { low: 5, medium: 25, high: 50 }
    });
  }

  /**
   * ALTERNATIVA 0: Post inicial (0-5% escrutado)
   */
  createInitialContent(data, params) {
    const fechasFormateadas = convertirFecha(data.last_update);
    const formatedProvinciaLocalidad = this.formatProvinciaLocalidad(data.provinciaLocalidad);

    return {
      title: `Elecciones 2023: aguardando resultados para Presidente en ${formatedProvinciaLocalidad}`,
      summary: `
        <ul>
          <li>Las urnas cerraron en ${formatedProvinciaLocalidad}.</li>
          <li>Los primeros datos del escrutinio presidencial se conocerán en las próximas horas.</li>
        </ul>`,
      subtitle: "",
      description: "",
      publishedDate: "2023-10-22T19:05:00.000Z",
      formattedPublishedDate: "22 OCT 2023",
      lastModified: "Sun, 22 Oct 2023 19:05:00 GMT",
      gtmPublishedDate: "2023-10-22T",
      modificatedAt: fechasFormateadas,
      body: [
        {
          type: "text",
          value: `<p>Las urnas cerraron en ${formatedProvinciaLocalidad} y el escrutinio provisorio para la elección presidencial comenzó. Los primeros datos oficiales se esperan en las próximas horas a medida que avance el conteo de votos.</p>`
        },
        {
          type: "text",
          value: `<p>Esta página se actualizará automáticamente con los resultados oficiales en tiempo real una vez que comiencen a procesarse las actas de las mesas de votación.</p>`
        },
        {
          type: "text",
          value: `<p>El futuro Presidente asumirá su cargo el 10 de diciembre, en reemplazo de Alberto Fernández. Coincidentemente, ese día se cumplirán 40 años de democracia ininterrumpidos en la Argentina, luego de que Raúl Alfonsín iniciara su mandato en 1983.</p>`
        },
        {
          _id: "iframe-map",
          type: "freehtml",
          value: {
            title: null,
            summary: null,
            name: `Mapa electoral - Presidente`,
            html: `<iframe class="elections-iframe" src="${params.urlIframe}" title="Resultados Presidente" scrolling="no" frameborder="0" data-external="1"></iframe>
              <script type="text/javascript" src="https://ecp.clarin.com/demo/js/iframeResizer.min.js"></script>
              <script type="text/javascript">iFrameResize({checkOrigin:false,heightCalculationMethod:'documentElementOffset',});</script>`,
            htmlAmp: null
          }
        },
        ...this.getRelatedLinks()
      ],
      authors: [],
      sections: this.getSections(),
      metadata: this.getMetadata()
    };
  }

  /**
   * ALTERNATIVA 1: Post temprano (5-25% escrutado)
   */
  createEarlyContent(data, params) {
    this.formatCandidates(data);
    const votosEnBlanco = this.extractStats(data.stats, "blank");
    const votosNulos = this.extractStats(data.stats, "annulled");
    const fechasFormateadas = convertirFecha(data.last_update);
    const formatedProvinciaLocalidad = this.formatProvinciaLocalidad(data.provinciaLocalidad);

    // Datos nacionales
    const nationalData = this.getNationalData(params.infoPresidentOrGovernors);

    return {
      title: `Resultados Elecciones 2023: quién gana para Presidente en ${formatedProvinciaLocalidad}`,
      summary: `
        <ul>
          <li>Qué candidato fue el más votado este domingo 22 de octubre.</li>
          <li>Toda la información actualizada y el mapa electoral de los comicios presidenciales en ${formatedProvinciaLocalidad}, minuto a minuto.</li>
        </ul>`,
      subtitle: "",
      description: "",
      publishedDate: "2023-10-22T19:05:00.000Z",
      formattedPublishedDate: "18 SEP 2023",
      lastModified: "Mon, 18 Sep 2023 23:01:20 GMT",
      gtmPublishedDate: "2023-09-18T",
      modificatedAt: fechasFormateadas,
      body: [
        {
          type: "text",
          value: `<p>Los ciudadanos de <strong>${formatedProvinciaLocalidad}</strong> acudieron nuevamente a las urnas 
            este domingo con motivo de las Elecciones presidenciales 2023 en Argentina. Según los resultados oficiales, 
            con el <strong>${data.mesasEscrutadas}%</strong> de mesas escrutadas, <strong>${data.ganador.candidates[0].fullname}</strong>, de <strong>${data.ganador.name}</strong>,
            obtenía <strong>${data.ganador.votes} votos</strong>, que representan el 
            <strong>${data.ganador.votes_percent}</strong> por ciento de los votos en este distrito.</p>`
        },
        {
          type: "text",
          value: `<p>En segundo lugar se ubicaba <strong>${data.segundo.candidates[0].fullname}</strong>, de <strong>${data.segundo.name}</strong> 
            con <strong>${data.segundo.votes}</strong> votos, que representan el <strong>${data.segundo.votes_percent}%</strong> por ciento de los sufragios. 
            En la tercera posición <strong>${data.tercero.candidates[0].fullname}</strong>, de <strong>${data.tercero.name}</strong>, alcanzaba 
            <strong>${data.tercero.votes}</strong> votos y un <strong>${data.tercero.votes_percent}</strong> por ciento de los sufragios.</p>`
        },
        {
          type: "text",
          value: `<p>De acuerdo con el escrutinio provisorio, <strong>${data.cuarto.candidates[0].fullname}</strong>, de <strong>${data.cuarto.name}</strong>,
            se ubicaba cuarto con <strong>${data.cuarto.votes}</strong> votos (<strong>${data.cuarto.votes_percent}</strong> por ciento)
            y, en la quinta posición, <strong>${data.quinto.candidates[0].fullname}</strong>, de <strong>${data.quinto.name}</strong>,
            con <strong>${data.quinto.votes}</strong> votos (<strong>${data.quinto.votes_percent}</strong> por ciento).</p>`
        },
        {
          type: "text",
          value: `<p>A nivel nacional, <strong>${nationalData.winner} (${nationalData.winnerParty})</strong> es quien
            lideraba los resultados electorales con el <strong>${nationalData.winnerPercent}</strong> por ciento de los sufragios,
            mientras que en segundo lugar se encontraba <strong>${nationalData.second}</strong> con el <strong>${nationalData.secondPercent}</strong> por
            ciento, seguido por <strong>${nationalData.third}</strong>, que obtenía el <strong>${nationalData.thirdPercent}</strong> por ciento de los
            votos, <strong>${nationalData.tablesPercent}</strong>% de mesas escrutadas</p>`
        },
        {
          type: "text",
          value: `<p>En tanto, el <strong>${votosEnBlanco[0].votes_percent}</strong> por ciento de los votos eran en blanco y un 
            <strong>${votosNulos[0].votes_percent}</strong> por ciento corresponde a votos nulos.</p>`
        },
        {
          type: "text",
          value: `<p>El futuro Presidente asumirá su cargo el 10 de diciembre, en reemplazo de Alberto Fernández. Coincidentemente,
            ese día se cumplirán 40 años de democracia ininterrumpidos en la Argentina, luego de que Raúl Alfonsín iniciara su mandato en 1983.</p>`
        },
        {
          type: "text",
          value: `<p>* Este artículo fue generado de forma automática sobre la base de los datos oficiales provistos por la Dirección Nacional Electoral (DINE).</p>`
        },
        {
          _id: "12345678",
          type: "freehtml",
          value: {
            title: null,
            summary: null,
            name: "INFO_PAIS_Intención de voto por fórmulas para la presidencial",
            html: `<iframe class="elections-iframe" src="${params.urlIframe}" title="Intención de voto por fórmulas para la presidencial" scrolling="no" frameborder="0" data-external="1"></iframe>
              <script type="text/javascript" src="https://ecp.clarin.com/demo/js/iframeResizer.min.js"></script><script type="text/javascript">iFrameResize({checkOrigin:false,heightCalculationMethod:'documentElementOffset',});</script>`,
            htmlAmp: null
          }
        },
        ...this.getRelatedLinks()
      ],
      authors: [],
      sections: this.getSections(),
      metadata: this.getMetadata()
    };
  }

  /**
   * ALTERNATIVA 2: Post parcial (25-50% escrutado)
   */
  createPartialContent(data, params) {
    this.formatCandidates(data);
    const votosEnBlanco = this.extractStats(data.stats, "blank");
    const votosNulos = this.extractStats(data.stats, "annulled");
    const fechasFormateadas = convertirFecha(data.last_update);
    const formatedProvinciaLocalidad = this.formatProvinciaLocalidad(data.provinciaLocalidad);

    // Datos nacionales
    const nationalData = this.getNationalData(params.infoPresidentOrGovernors);

    return {
      title: `Resultados Elecciones 2023 en ${formatedProvinciaLocalidad}: quién gana para Presidente`,
      summary: `
        <ul>
          <li>Qué candidato es el más votado esta jornada democrática.</li>
          <li>Minuto a minuto, toda la información actualizada y el mapa electoral de los comicios presidenciales.</li>
        </ul>`,
      subtitle: "",
      description: "",
      publishedDate: "2023-10-22T19:05:00.000Z",
      formattedPublishedDate: "18 SEP 2023",
      lastModified: "Mon, 18 Sep 2023 23:01:20 GMT",
      gtmPublishedDate: "2023-09-18T",
      modificatedAt: fechasFormateadas,
      body: [
        {
          type: "text",
          value: `<p>Los electores de <strong>${formatedProvinciaLocalidad}</strong> acudieron nuevamente a las urnas
            este domingo en el marco de las Elecciones presidenciales 2023. Según los datos oficiales del escrutinio provisorio, 
            <strong>${data.ganador.candidates[0].fullname}</strong>, de <strong>${data.ganador.name}</strong>, 
            era la persona más votada para Presidente en este distrito, 
            con <strong>${data.ganador.votes}</strong> votos, lo que representa el <strong>${data.ganador.votes_percent}</strong> por ciento de los votos.</p>`
        },
        {
          type: "text",
          value: `<p><strong>Con el ${data.mesasEscrutadas}</strong> por ciento de las mesas escrutadas,<strong> 
            los resultados de las elecciones 2023 en 
            ${formatedProvinciaLocalidad} son:</strong></p>`
        },
        {
          type: "text",
          value: `<ol>
            <li>${data.ganador.candidates[0].fullname}, de ${data.ganador.name}, con ${data.ganador.votes} y el ${data.ganador.votes_percent}% de los votos</li>
            <li>${data.segundo.candidates[0].fullname}, de ${data.segundo.name}, con ${data.segundo.votes} y el ${data.segundo.votes_percent}% de los votos</li>
            <li>${data.tercero.candidates[0].fullname}, de ${data.tercero.name}, con ${data.tercero.votes} y el ${data.tercero.votes_percent}% de los votos</li>
            <li>${data.cuarto.candidates[0].fullname}, de ${data.cuarto.name}, con ${data.cuarto.votes} y el ${data.cuarto.votes_percent}% de los votos</li>
            <li>${data.quinto.candidates[0].fullname}, de ${data.quinto.name}, con ${data.quinto.votes} y el ${data.quinto.votes_percent}% de los votos</li>
            <li>Votos en blanco: el ${votosEnBlanco[0].votes_percent}% fueron votos en blanco</li>
            <li>Votos nulos: el ${votosNulos[0].votes_percent}%</li>
          </ol>`
        },
        {
          type: "text",
          value: `<p>A nivel nacional, <strong>${nationalData.winner} (${nationalData.winnerParty})</strong> es quien
            lideraba los resultados electorales con el <strong>${nationalData.winnerPercent}</strong> por ciento de los sufragios,
            mientras que en segundo lugar se encontraba <strong>${nationalData.second}</strong> con el <strong>${nationalData.secondPercent}</strong> por
            ciento, seguido por <strong>${nationalData.third}</strong>, que obtenía el <strong>${nationalData.thirdPercent}</strong> por ciento de los
            votos, con el<strong>${nationalData.tablesPercent}</strong>% de mesas escrutadas</p>`
        },
        {
          type: "text",
          value: `<p>El futuro Presidente asumirá su cargo el 10 de diciembre, en reemplazo de Alberto Fernández. Coincidentemente,
            ese día se cumplirán 40 años de democracia ininterrumpidos en la Argentina, luego de que Raúl Alfonsín iniciara su mandato en 1983.</p>`
        },
        {
          _id: "12345678",
          type: "freehtml",
          value: {
            title: null,
            summary: null,
            name: "INFO_PAIS_Intención de voto por fórmulas para la presidencial",
            html: `<iframe class="elections-iframe" src="${params.urlIframe}" title="Intención de voto por fórmulas para la presidencial" scrolling="no" frameborder="0" data-external="1"></iframe>
              <script type="text/javascript" src="https://ecp.clarin.com/demo/js/iframeResizer.min.js"></script><script type="text/javascript">iFrameResize({checkOrigin:false,heightCalculationMethod:'documentElementOffset',});</script>`,
            htmlAmp: null
          }
        },
        ...this.getRelatedLinks()
      ],
      authors: [],
      sections: this.getSections(),
      metadata: this.getMetadata()
    };
  }

  /**
   * ALTERNATIVA 3: Post final (>50% escrutado)
   */
  createFinalContent(data, params) {
    this.formatCandidates(data);
    const votosEnBlanco = this.extractStats(data.stats, "blank");
    const votosNulos = this.extractStats(data.stats, "annulled");
    const fechasFormateadas = convertirFecha(data.last_update);
    const formatedProvinciaLocalidad = this.formatProvinciaLocalidad(data.provinciaLocalidad);

    // Datos nacionales
    const nationalData = this.getNationalData(params.infoPresidentOrGovernors);

    // Lista dinámica de candidatos
    const candidatesList = this.buildCandidatesList(data);

    return {
      title: `Resultados Elecciones 2023: quién gana para Presidente en ${formatedProvinciaLocalidad}`,
      summary: `
        <ul>
          <li>Qué candidato fue el más votado en ${formatedProvinciaLocalidad}.</li>
          <li>Toda la información actualizada y el mapa electoral de los comicios presidenciales, minuto a minuto.</li>
        </ul>`,
      subtitle: "",
      description: "",
      publishedDate: "2023-10-22T19:05:00.000Z",
      formattedPublishedDate: "18 SEP 2023",
      lastModified: "Mon, 18 Sep 2023 23:01:20 GMT",
      gtmPublishedDate: "2023-09-18T",
      modificatedAt: fechasFormateadas,
      body: [
        {
          type: "text",
          value: `<p>Los ciudadanos de <strong>${formatedProvinciaLocalidad}</strong> acudieron nuevamente a las urnas 
            este domingo con motivo de las Elecciones presidenciales 2023 en Argentina. Según los resultados oficiales del escrutinio provisorio, 
            <strong>${data.ganador.candidates[0].fullname}</strong>, de <strong>${data.ganador.name}</strong>,
            es la persona más votada para Presidente en este distrito, con <strong>${data.ganador.votes}</strong> votos, que representan el <strong>${data.ganador.votes_percent}</strong> por ciento de los votos.
          </p>`
        },
        {
          type: "text",
          value: `<p><strong>Con el ${data.mesasEscrutadas}</strong> por ciento de las mesas escrutadas,<strong> 
            los resultados de las elecciones 2023 en 
            ${formatedProvinciaLocalidad} son:</strong></p>`
        },
        {
          type: "text",
          value: candidatesList
        },
        {
          type: "text",
          value: `<p>Asimismo, el <strong>${votosEnBlanco[0].votes_percent}</strong> por ciento de los votos son en blanco y un
            <strong>${votosNulos[0].votes_percent}</strong> por ciento corresponde a votos nulos.</p>`
        },
        {
          type: "text",
          value: `<p>A nivel nacional, <strong>${nationalData.winner} (${nationalData.winnerParty})</strong> es quien
            lideraba los resultados electorales con el <strong>${nationalData.winnerPercent}</strong> por ciento de los sufragios,
            mientras que en segundo lugar se encuentra <strong>${nationalData.second}</strong> con el <strong>${nationalData.secondPercent}</strong> por
            ciento, seguido por <strong>${nationalData.third}</strong>, que obtiene el <strong>${nationalData.thirdPercent}</strong> por ciento de los
            votos.</p>`
        },
        {
          type: "text",
          value: `<p>El futuro Presidente asumirá su cargo el 10 de diciembre, en reemplazo de Alberto Fernández. Coincidentemente,
            ese día se cumplirán 40 años de democracia ininterrumpidos en la Argentina, luego de que Raúl Alfonsín iniciara su mandato en 1983.</p>`
        },
        {
          type: "text",
          value: `<p>* Este artículo fue generado de forma automática sobre la base de los datos oficiales provistos por la Dirección Nacional Electoral (DINE).</p>`
        },
        {
          _id: "12345678",
          type: "freehtml",
          value: {
            title: null,
            summary: null,
            name: "INFO_PAIS_Intención de voto por fórmulas para la presidencial",
            html: `<iframe class="elections-iframe" src="${params.urlIframe}" title="Intención de voto por fórmulas para la presidencial" scrolling="no" frameborder="0" data-external="1"></iframe>
              <script type="text/javascript" src="https://ecp.clarin.com/demo/js/iframeResizer.min.js"></script><script type="text/javascript">iFrameResize({checkOrigin:false,heightCalculationMethod:'documentElementOffset',});</script>`,
            htmlAmp: null
          }
        },
        ...this.getRelatedLinks()
      ],
      authors: [],
      sections: this.getSections(),
      metadata: this.getMetadata()
    };
  }

  /**
   * Helpers específicos de presidente
   */
  formatCandidates(data) {
    const propiedades = ["ganador", "segundo", "tercero", "cuarto", "quinto"];
    propiedades.forEach(prop => {
      if (data[prop]) {
        data[prop].votes = formatWithCommas(data[prop].votes);
        data[prop].votes_percent = roundToTwoDecimals(data[prop].votes_percent);
      }
    });
    data.mesasEscrutadas = roundToTwoDecimals(data.mesasEscrutadas);
  }

  extractStats(stats, type) {
    const result = stats?.filter(item => item.type === type) || [];
    if (result[0]) {
      result[0].votes_percent = roundToTwoDecimals(result[0].votes_percent);
    }
    return result;
  }

  formatProvinciaLocalidad(cadena) {
    const partes = cadena.split(", ");
    if (partes.length === 2) {
      const [provincia, localidad] = partes;
      return `${localidad}, ${provincia}`;
    }
    return cadena;
  }

  getNationalData(infoPresidentOrGovernors) {
    if (!infoPresidentOrGovernors?.data?.parties) {
      return {
        winner: '',
        winnerParty: '',
        winnerPercent: '0',
        second: '',
        secondPercent: '0',
        third: '',
        thirdPercent: '0',
        tablesPercent: '0'
      };
    }

    const parties = infoPresidentOrGovernors.data.parties;
    return {
      winner: parties[0]?.candidates?.[0]?.fullname || '',
      winnerParty: parties[0]?.name || '',
      winnerPercent: parties[0]?.votes_percent || '0',
      second: parties[1]?.candidates?.[0]?.fullname || '',
      secondPercent: parties[1]?.votes_percent || '0',
      third: parties[2]?.candidates?.[0]?.fullname || '',
      thirdPercent: parties[2]?.votes_percent || '0',
      tablesPercent: infoPresidentOrGovernors.data.tables_percent || '0'
    };
  }

  buildCandidatesList(data) {
    let candidatesList = "<ol>";

    const positions = ['ganador', 'segundo', 'tercero', 'cuarto', 'quinto'];
    
    positions.forEach(position => {
      if (data[position]?.candidates?.length > 0) {
        candidatesList += `<li>${data[position].candidates[0].fullname}, de ${data[position].name}, con ${data[position].votes} y el ${data[position].votes_percent}% de los votos</li>`;
      }
    });

    return candidatesList + "</ol>";
  }

  getRelatedLinks() {
    return [
      {
        type: "text",
        value: `<hr class="separator" /><h2 class="read-more">Seguí leyendo</h2>`
      },
      {
        type: "text",
        value: `<h2 class="more-links"><a href="https://www.clarin.com/politica/pasa-voto-22-octubre_0_WoZDqEIlAu.html" target="_blank">
          Elecciones 2023: qué pasa si no voté</a></h2>`
      },
      {
        type: "text",
        value: `<h2 class="more-links"><a href="https://www.clarin.com/politica/multa-votar-dia-puede-pagar_0_Lh7yzMdp4G.html" target="_blank">
          De cuánto es la multa por no votar</a></h2>`
      },
      {
        type: "text",
        value: `<h2 class="more-links"><a href="https://www.clarin.com/politica/elecciones-2023-votos-necesitan-ganar-primera-vuelta_0_5GZaXJgLnt.html" target="_blank">
          Elecciones 2023: ¿cuántos votos se necesitan para ganar en primera vuelta?</a></h2>`
      },
      {
        type: "text",
        value: `<h2 class="more-links"><a href="https://www.clarin.com/politica/elecciones-2023-balotaje_0_lsy8JH3XN3.html" target="_blank">
          ¿Qué es el balotaje o segunda vuelta?</a></h2>`
      }
    ];
  }

  getSections() {
    return [
      {
        _id: 15006,
        name: "Información General",
        url: "informacion-general",
        nsSite: "",
        autoplay: false,
        metadata: {}
      }
    ];
  }

  getMetadata() {
    return {
      typeWeb: "small",
      origin: "default",
      payWallAccessControl: "freemium",
      sponsored: false,
      accessProduct: "CLARIN_D",
      canHaveAmp: true,
      canHaveComments: true,
      autoRefresh: true,
      titleEnable: false,
      footer_visible: true,
      ualterDisabled: false,
      trinityDisabled: false,
      disabledTimelineLiveblog: false,
      featuredContent: false,
      stopWords: true
    };
  }

  getTags(params) {
    return [
      { 
        name: `Elecciones ${params.year || 2023}`, 
        slug: `elecciones-${params.year || 2023}`, 
        subject: "subject", 
        _id: "VyFmuMo-2" 
      },
      { 
        name: "Presidente", 
        slug: "presidente", 
        subject: "subject" 
      }
    ];
  }

  generateMeta(content, data, params) {
    const title = content?.title || `Elecciones Presidente ${params.year || 2023} - ${data.provincia}`;
    const description = this.extractDescription(content) || 
                       `Resultados elecciones a Presidente en ${data.provincia}`;
    const imageUrl = this.getImage(params.codeLetter);

    return {
      description,
      title,
      keywords: `Presidente, ${data.provincia}, Elecciones ${params.year || 2023}, Resultados`,
      url: params.baseUrl,
      urlImage: imageUrl,
      extra: [
        { name: "title", content: title },
        { name: "description", content: description },
        { name: "distribution", content: "global" },
        { name: "robots", content: "max-image-preview:large" },
        { property: "og:image", content: imageUrl },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "article:published_time", content: `${params.year || 2023}-10-22T19:05:00.000Z` },
        { property: "article:modified_time", content: data.last_update }
      ]
    };
  }

  extractDescription(content) {
    return content?.description || 
           content?.body?.[0]?.value?.replace(/<[^>]*>/g, '')?.trim();
  }
}